import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { User } from '../models/User';
import { useAppContext } from '../contexts/Storage';
import { 
  AuthService, 
  LoginCredentials, 
  SignupCredentials, 
  GoogleSignupCredentials,
  UpdateUserData,
  ResetPasswordWithCodeData,
  AuthResponse 
} from '../services/authService';

export const useAuth = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const appContext = useAppContext();
  const authService = new AuthService(() => appContext);

  const login = useCallback(async (credentials: LoginCredentials) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await authService.login(credentials);

      if (result.success && result.data) {
        const { access_token, user } = result.data;
        
        const userObj = new User({
          id: user.id,
          username: user.username,
          email: user.email,
          imageUrl: user.image_url,
          firstName: user.first_name,
          lastName: user.last_name,
          accessToken: access_token
        });
        
        authService.saveUserSession(userObj);
        
        toast.success('Login successful!');
        const redirectPath = sessionStorage.getItem('redirectAfterLogin') || '/dashboard';
        sessionStorage.removeItem('redirectAfterLogin');
        navigate(redirectPath);
        return { success: true };
      } else {
        setError(result.error || 'Login failed');
        toast.error(result.error || 'Login failed');
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, [navigate, authService]);

  const signup = useCallback(async (credentials: SignupCredentials) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await authService.signup(credentials);

      if (result.success) {
        toast.success('Account created successfully!');
        navigate('/login');
        return { success: true };
      } else {
        setError(result.error || 'Signup failed');
        toast.error(result.error || 'Signup failed');
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, [navigate, authService]);

  const sendVerificationCode = useCallback(async (email: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await authService.sendVerificationCode(email);

      if (result.success) {
        toast.success(result.message || 'Verification code sent to your email');
        return { success: true };
      } else {
        setError(result.error || 'Failed to send verification code');
        toast.error(result.error || 'Failed to send verification code');
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, [authService]);

  const updateUser = useCallback(async (data: UpdateUserData) => {
    setIsLoading(true);
    setError(null);

    try {
      // Get token from context
      const token = appContext.token;
      if (!token) {
        throw new Error('No authentication token found');
      }

      const result = await authService.updateUser(token, data);

      if (result.success) {
        toast.success(result.message || 'Update successful');
        return { success: true };
      } else {
        setError(result.error || 'Failed to update');
        toast.error(result.error || 'Failed to update');
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, [authService, appContext.token]);

  const resetPasswordWithCode = useCallback(async (data: ResetPasswordWithCodeData) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await authService.resetPasswordWithCode(data);

      if (result.success) {
        toast.success(result.message || 'Password reset successfully');
        return { success: true };
      } else {
        setError(result.error || 'Failed to reset password');
        toast.error(result.error || 'Failed to reset password');
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, [authService]);

  const loginWithGoogle = useCallback(async (googleToken: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await authService.loginWithGoogle(googleToken);

      if (result.success && result.data) {
        const { access_token, user } = result.data;
        
        const userObj = new User({
          id: user.id,
          username: user.username,
          email: user.email,
          imageUrl: user.image_url,
          firstName: user.first_name,
          lastName: user.last_name,
          accessToken: access_token
        });
        
        authService.saveUserSession(userObj);
        
        toast.success('Login successful!');
        const redirectPath = sessionStorage.getItem('redirectAfterLogin') || '/dashboard';
        sessionStorage.removeItem('redirectAfterLogin');
        navigate(redirectPath);
        return { success: true };
      } else {
        setError(result.error || 'Google login failed');
        toast.error(result.error || 'Google login failed');
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, [navigate, authService]);

  const signupWithGoogle = useCallback(async (googleToken: string, username?: string, email?: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const credentials: GoogleSignupCredentials = {
        google_token: googleToken,
        username,
        email
      };

      const result = await authService.signupWithGoogle(credentials);

      if (result.success && result.data) {
        const { access_token, user } = result.data;
        
        const userObj = new User({
          id: user.id,
          username: user.username,
          email: user.email,
          imageUrl: user.image_url,
          firstName: user.first_name,
          lastName: user.last_name,
          accessToken: access_token
        });
        
        authService.saveUserSession(userObj);
        
        if (result.success) {
          toast.success(result.message || 'Account created successfully!');
        }
        
        navigate('/dashboard');
        return { success: true };
      } else {
        setError(result.error || 'Google signup failed');
        // Show exact backend error message
        toast.error(result.error || 'Google signup failed');
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, [navigate, authService]);

  const logout = useCallback(() => {
    authService.logout();
    toast.success('Logged out successfully');
    navigate('/login');
  }, [navigate, authService]);

  const isAuthenticated = useCallback(() => {
    return authService.isAuthenticated();
  }, [authService]);

  return {
    login,
    signup,
    sendVerificationCode,
    updateUser,
    resetPasswordWithCode,
    loginWithGoogle,
    signupWithGoogle,
    logout,
    isAuthenticated,
    isLoading,
    error
  };
};